import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../app-context";
import { SearchProps } from "../components/search/Search";
import { LocationSearchParamsOption, searchUsingPlacesAPI } from "../_services/city-search.service";
import { STATE_LIST } from "../constants/StateList";
import { CitySearchResultWithCateg, PlaceSearchResult } from "../custom-types/location-search";
import { getCurrentLocation } from "../methods/getCurrentLocation";
import { useCsDashboard } from "./useCsDashboard";

interface Params<T> {
    defaultOptions?: T[];
    onSelectItem?: SearchProps<CitySearchResultWithCateg>["onSelectItem"];
    showCurrentLocationOption?: boolean;
    locationParamsOption: LocationSearchParamsOption;
}

function useSearch<T>({ defaultOptions, onSelectItem, showCurrentLocationOption, locationParamsOption }: Params<T>) {
    const [searchText, setSearchText] = useState("");
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [_defaultOptions, setDefaultOptions] = useState<T[]>();
    const [searchOptions, setSearchOptions] = useState<T[]>([]);
    const [selectedItem, setSelectedItem] = useState<CitySearchResultWithCateg | undefined>();
    const { isCsDashboardUser } = useCsDashboard();

    const onInputChange = useCallback(
        (input: string) => {
            input === "" && setSearchOptions(_defaultOptions || []);
        },
        [defaultOptions]
    );

    const onFocus = () => {
        setSearchOptions(_defaultOptions || []);
    };

    const _onItemSelected: SearchProps<CitySearchResultWithCateg>["onSelectItem"] = (selectedItems) => {
        if (selectedItems?.[0]?.category === "currentLoc") {
            setSelectedItem(undefined);
        } else {
            setSelectedItem(selectedItems[0]);
        }
        onSelectItem?.(selectedItems);
    };

    useEffect(() => {
        if (showCurrentLocationOption && !isCsDashboardUser) {
            setDefaultOptions([{ name: "Use Current City", category: "currentLoc" } as any, ...(defaultOptions || [])]);
        } else {
            setDefaultOptions(defaultOptions);
        }
    }, [showCurrentLocationOption, defaultOptions]);

    useEffect(() => {
        _defaultOptions && setSearchOptions(_defaultOptions);
    }, [_defaultOptions]);

    return {
        searchText,
        setSearchText,
        isSearchLoading,
        setIsSearchLoading,
        searchOptions,
        setSearchOptions,
        onInputChange,
        _onItemSelected,
        selectedItem,
        setSelectedItem,
        onFocus
    };
}

export function useStateSearch({
    defaultOptions,
    onSelectItem,
    showCurrentLocationOption,
    locationParamsOption
}: Params<CitySearchResultWithCateg>) {
    const {
        isSearchLoading,
        onInputChange,
        searchOptions,
        searchText,
        setIsSearchLoading,
        setSearchText,
        setSearchOptions,
        _onItemSelected,
        selectedItem,
        setSelectedItem,
        onFocus
    } = useSearch({
        locationParamsOption,
        defaultOptions,
        onSelectItem,
        showCurrentLocationOption
    });
    const labelKey: SearchProps<CitySearchResultWithCateg>["labelKey"] = ({ state }) => state || "";

    const onSearchInput: SearchProps<CitySearchResultWithCateg>["onSearch"] = useCallback(async (query) => {
        query = decodeURIComponent(query);
        setIsSearchLoading(true);
        setSearchText(query);
        const options = STATE_LIST.filter((s) => s.name.match(new RegExp("^" + query, "i")));
        setSearchOptions(options);
        setIsSearchLoading(false);
    }, []);

    return {
        searchText,
        labelKey,
        setSearchText,
        isSearchLoading,
        setIsSearchLoading,
        searchOptions,
        setSearchOptions,
        onInputChange,
        onSearchInput,
        onItemSelected: _onItemSelected,
        selectedItem,
        setSelectedItem,
        onFocus
    };
}

export function usePlaceSearch({
    defaultOptions,
    onSelectItem,
    showCurrentLocationOption,
    locationParamsOption
}: Params<CitySearchResultWithCateg>) {
    const { updateRecentTransporterCitySearches } = useContext(AppContext);
    const {
        isSearchLoading,
        onInputChange,
        searchOptions,
        searchText,
        setIsSearchLoading,
        setSearchText,
        setSearchOptions,
        _onItemSelected,
        selectedItem,
        setSelectedItem,
        onFocus
    } = useSearch({
        defaultOptions,
        onSelectItem,
        showCurrentLocationOption,
        locationParamsOption
    });
    const labelKey: SearchProps<CitySearchResultWithCateg>["labelKey"] = ({ name }) => {
        return name ? name : "";
    };

    const onSearchInput: SearchProps<CitySearchResultWithCateg>["onSearch"] = useCallback(async (query) => {
        setIsSearchLoading(true);

        setSearchText(query);
        try {
            const options = (await searchUsingPlacesAPI(query, locationParamsOption)) as PlaceSearchResult[];
            if (options.length !== 0)
                setSearchOptions(
                    options.map((option, i) => {
                        return {
                            id: i,
                            name: option.description,
                            state_id: i,
                            state_code: "",
                            state: option.state,
                            place_id: option.place_id,
                            city: option.city
                        };
                    })
                );
        } catch (err) {
            console.error(err);
        } finally {
            setIsSearchLoading(false);
        }
    }, []);

    const __onItemSelected: typeof _onItemSelected = async (selectedItems) => {
        if (selectedItems?.[0]?.category === "currentLoc") {
            setIsSearchLoading(true);
            const currLoc = await getCurrentLocation(true, locationParamsOption);
            if (!currLoc || currLoc.status === "failed") {
                setIsSearchLoading(false);
                return;
            }
            _onItemSelected?.([currLoc]);
            setIsSearchLoading(false);
            return;
        }
        if (selectedItems[0]) updateRecentTransporterCitySearches?.(selectedItems[0]);
        _onItemSelected?.(selectedItems);
        return;
    };

    return {
        labelKey,
        searchText,
        setSearchText,
        isSearchLoading,
        setIsSearchLoading,
        searchOptions,
        setSearchOptions,
        onInputChange,
        onSearchInput,
        onItemSelected: __onItemSelected,
        selectedItem,
        setSelectedItem,
        onFocus
    };
}
